// ============================================================================
// PortalPanel — TRAINER-side client-portal management (on the client detail
// page). Shows the client's portal status (none / invited / active), an
// "Invite to portal" action that calls POST /api/clients/{id}/invite, and the
// returned one-time claim link with a copy button. On an already-active client
// it confirms the portal is live and notes that self-logged entries appear in
// the client's history. Built on the locked Pulse design system.
// ============================================================================
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge, Banner, Button, Card, CardHeader } from "../../components";
import type { BadgeTone } from "../../components";
import { clientsApi, qk } from "../../api";
import type { Client, InviteResponse, PortalStatus } from "../../api";
import { fmtDate } from "./lib";

const STATUS_TONE: Record<string, BadgeTone> = {
  none: "neutral",
  invited: "warning",
  active: "success",
};

export function PortalPanel({ client }: { client: Client }) {
  const { t } = useTranslation("clientDetail");
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  // The freshly-minted link (kept in component state so re-invites surface the
  // newest link; on first load we fall back to rebuilding it from the stored
  // token if the client is still 'invited').
  const [link, setLink] = useState<string | null>(null);
  // Whether the last invite ALSO emailed the claim link (Wave 6). null = no
  // invite has run this mount yet (so we show the "will email" hint instead).
  const [emailedLast, setEmailedLast] = useState<boolean | null>(null);

  const status = (client.portal_status as PortalStatus | undefined) ?? "none";
  // The backend emails the claim link automatically when the client has an
  // email on file; surface that affordance in the UI.
  const hasEmail = Boolean(client.email);

  const invite = useMutation({
    mutationFn: () => clientsApi.invite(client.id),
    onSuccess: (res: InviteResponse) => {
      setLink(res.invite_url);
      setCopied(false);
      setEmailedLast(res.email_sent);
      void qc.invalidateQueries({ queryKey: qk.clients.detail(client.id) });
    },
  });

  // A still-pending invite carries its one-time token on the client row, so we
  // can rebuild the link without re-inviting (the trainer reloaded the page).
  const storedLink =
    link ??
    (status === "invited" && client.invite_token
      ? `${window.location.origin}/client/claim?token=${client.invite_token}`
      : null);

  const copy = async () => {
    if (!storedLink) return;
    try {
      await navigator.clipboard.writeText(storedLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const isActive = status === "active";
  const isInvited = status === "invited";

  return (
    <Card>
      <CardHeader
        title={t("portal.title")}
        subtitle={t("portal.blurb")}
        action={
          <Badge tone={STATUS_TONE[status] ?? "neutral"} dot>
            {t(`portal.status.${status}`)}
          </Badge>
        }
      />

      {isActive ? (
        <div className="space-y-3">
          <Banner tone="success" title={t("portal.activeTitle")}>
            {t("portal.activeBlurb")}
          </Banner>
          <div className="rounded-md bg-surface-sunken px-4 py-3">
            <p className="font-display text-sm font-bold text-body">
              {t("portal.selfLogged")}
            </p>
            <p className="mt-0.5 text-sm text-body-soft">
              {t("portal.selfLoggedBlurb")}
            </p>
          </div>
          {/* Email-delivery confirmation for the most recent (re)invite. */}
          {emailedLast === true && (
            <Banner tone="success" title={t("portal.emailSentTitle")}>
              {t("portal.emailSentBlurb", { email: client.email })}
            </Banner>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => invite.mutate()}
            disabled={invite.isPending}
          >
            {invite.isPending
              ? t("portal.inviting")
              : hasEmail
                ? t("portal.reinviteEmail")
                : t("portal.reinvite")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {isInvited && client.invite_sent_at && (
            <p className="text-sm text-body-soft">
              {t("portal.invitedOn", { date: fmtDate(client.invite_sent_at) })}
            </p>
          )}

          {/* Email-delivery confirmation for the most recent invite. */}
          {emailedLast === true && (
            <Banner tone="success" title={t("portal.emailSentTitle")}>
              {t("portal.emailSentBlurb", { email: client.email })}
            </Banner>
          )}

          {storedLink && (
            <div className="rounded-md bg-violet-soft p-4">
              <p className="font-display text-sm font-bold text-ember-600">
                {t("portal.linkTitle")}
              </p>
              <p className="mt-0.5 text-sm text-body-soft">{t("portal.linkBlurb")}</p>
              <div className="mt-3 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-sm bg-surface px-3 py-2 text-xs text-body-muted ring-1 ring-line">
                  {storedLink}
                </code>
                <Button size="sm" variant="secondary" onClick={() => void copy()}>
                  {copied ? t("portal.copied") : t("portal.copy")}
                </Button>
              </div>
            </div>
          )}

          {invite.isError && <Banner tone="danger" title={t("portal.error")} />}

          <div className="space-y-2">
            <Button
              onClick={() => invite.mutate()}
              disabled={invite.isPending}
              iconLeft={
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
                  <path d="M4 12h16M14 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
            >
              {invite.isPending
                ? t("portal.inviting")
                : isInvited
                  ? hasEmail
                    ? t("portal.reinviteEmail")
                    : t("portal.reinvite")
                  : hasEmail
                    ? t("portal.inviteEmail")
                    : t("portal.invite")}
            </Button>
            {/* Tell the trainer the link will ALSO be emailed (or that there's
                no email on file, so it's copy-link only). */}
            <p className="text-sm text-body-faint">
              {hasEmail
                ? t("portal.willEmail", { email: client.email })
                : t("portal.noEmail")}
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}
